<template>
	<mdb-row>
		<edit-table ref="table" class="mr-lg-2 pb-1 lists-table" :columns="columns" :data="data" :current-page="page" @page="changePage" @pages="setIds" @update="addEntity" @delete="removeEntity" @sync="fetchListsEntities">
			<template v-slot:pre-fieldset>
				<mdb-col>
					<mdb-select :btn-save="false" :options="listSelectOptions(listFilter)" @change="listFilter = $event"></mdb-select>
				</mdb-col>
				<mdb-col>
					<mdb-select :btn-save="false" :options="definitionSelectOptions(definitionFilter)" @change="definitionFilter = $event"></mdb-select>
				</mdb-col>
			</template>
			<template v-slot:pre-th>
				<th>
					<mdb-input :key="updatedKey" v-if="ids.length" type="checkbox" id="selected-all" name="selected" :value="allSelected" @change="selectAll($event)"></mdb-input>
				</th>
			</template>
			<template v-slot:post-th>
				<th class="py-0">
					<mdb-btn @click="removeSelected" color="danger" size="sm" rounded>Remove selected</mdb-btn>
				</th>
			</template>
			<template v-slot:pre-th-foot>
				<th></th>
			</template>
			<template v-slot:pre-td="{ row }">
				<td>
					<mdb-input :key="updatedKey" v-if="!isNew(row)" type="checkbox" :id="'selected-' + id(row)" name="selected" :value="isSelected(row)" @change="select(row, $event)"></mdb-input>
				</td>
			</template>
			<template v-slot:__listName="{ field, row, index, value }">
				<mdb-select :btn-save="false" :options="listOptions(value)" @change="change({ field, row, index, value: $event })"></mdb-select>
			</template>
			<template v-slot:__definitionName="{ field, row, index, value }">
				<mdb-select :btn-save="false" :options="definitionOptions(value)" @change="change({ field, row, index, value: $event })"></mdb-select>
			</template>
			<template v-slot:__added="{ value }">
				<div>{{ formatDate(value) }}</div>
			</template>
			<template v-slot:post-table>
				<hr/>
				<mdb-row>
					<mdb-col>
						<mdb-select :btn-save="false" :options="listSelectOptions(uploadList)" @change="uploadList = $event"></mdb-select>
					</mdb-col>
					<mdb-col>
						<mdb-select :btn-save="false" :options="definitionSelectOptions(uploadDefinition)" @change="uploadDefinition = $event"></mdb-select>
					</mdb-col>
					<mdb-col>
						<mdb-input :value="uploadReason" @change="uploadReason = $event" :placeholder="$t('lists.reason-label', locale)"></mdb-input>
					</mdb-col>
				</mdb-row>
				<mdb-row>
					<mdb-col>
						<mdb-file-input multiple btnColor="default" :disabled="!uploadList || !uploadDefinition || !uploadReason" @getValue="uploadFiles = [... $event]"/>
					</mdb-col>
					<mdb-col>
						<mdb-btn :save="true" :block="true" size="sm" m="t2" color="default" @click="handleUpload">Загрузить</mdb-btn>
					</mdb-col>
				</mdb-row>
				<hr/>
				<mdb-row>
					<mdb-col>
						<mdb-input @change="newList = $event"></mdb-input>
					</mdb-col>
					<mdb-col class="text-left">
						<mdb-btn @click="add" color="success" size="sm" rounded>{{ $t('lists.add', locale) }}</mdb-btn>
					</mdb-col>
				</mdb-row>
				<mdb-row>
					<mdb-col>
						<mdb-select :btn-save="false" :options="listSelectionOptions" @change="selectedList = $event"></mdb-select>
					</mdb-col>
					<mdb-col class="text-left">
						<mdb-btn @click="remove" color="danger" size="sm" rounded>{{ $t('lists.remove', locale) }}</mdb-btn>
					</mdb-col>
				</mdb-row>
			</template>
		</edit-table>
	</mdb-row>
</template>

<script>
import {mapActions, mapState} from 'vuex'

import {mdbBtn, mdbFileInput, mdbIcon, mdbInput, mdbSelect} from '@/components/mdbvue'
import {arrayEquals, formatValue} from '@/helpers/javascript'
import {isListableDefinition} from '@/helpers/lists'
import EditTable from '@/components/common/EditTable'

export default {
	name: 'Lists',
	components: { mdbBtn, mdbFileInput, mdbIcon, mdbInput, mdbSelect, EditTable },
	data () {
		return {
			selected: {},
			ids: [],
			listFilter: null,
			definitionFilter: null,
			uploadList: null,
			uploadDefinition: null,
			uploadReason: null,
			uploadFiles: [],
			selectedList: null,
			newList: null,
			updatedKey: null,
			page: 0
		}
	},
	computed: {
		columns () {
			return [
				{
					label: this.$t('lists.list-label', this.locale),
					field: 'listName'
				},
				{
					label: this.$t('lists.definition-label', this.locale),
					field: 'definitionName'
				},
				{
					label: this.$t('lists.value-label', this.locale),
					field: 'value'
				},
				{
					label: this.$t('lists.reason-label', this.locale),
					field: 'reason'
				},
				{
					label: this.$t('lists.added-label', this.locale),
					field: 'added',
					readonly: true
				}
			]
		},
		listSelectionOptions () {
			return Object.values(this.lists).map(l => ({ value: l.name, text: l.name, selected: l.name === this.selectedList }))
		},
		data () {
			return this.listsEntities.filter(e => (!this.definitionFilter || e.definitionName === this.definitionFilter) && (!this.listFilter || e.listName === this.listFilter))
				.map(e => Object.assign({}, e, { key: this.id(e) }))
		},
		allSelected () {
			return !this.ids || arrayEquals(Object.entries(this.selected).filter(e => e[1]).map(e => e[0]), this.ids)
		},
		definitions () {
			return Object.values(this.entities).filter(e => e.type === 'String' && isListableDefinition(e.name))
		},
		...mapState(['locale']),
		...mapState('lists', ['lists', 'listsEntities']),
		...mapState('schema', ['entities'])
	},
	async mounted () {
		await this.fetchListsEntities()
		this.resetUpdatedKey()
	},
	watch: {
		async lists () {
			await this.fetchListsEntities()
		},
		async $route () {
			await this.fetchListsEntities()
		}
	},
	methods: {
		change ({ field, row, index, value }) {
			this.$refs.table.change({ field, row, index, value })
		},
		listOptions (value) {
			return [{ text: this.$t('lists.list-select', this.locale), value: null, disabled: true, selected: !value }]
				.concat(Object.values(this.lists).map(l => ({ value: l.name, text: l.name, selected: l.name === value })))
		},
		listSelectOptions (value) {
			return this.listOptions(value).concat({ value: null, text: this.$t('common.reset', this.locale), selected: !value })
		},
		definitionOptions (value) {
			return [{ text: this.$t('lists.definition-select', this.locale), value: null, disabled: true, selected: !value }]
				.concat(this.definitions.map(d => ({ value: d.name, text: d.name, selected: d.name === value })))
		},
		definitionSelectOptions (value) {
			return this.definitionOptions(value).concat({ value: null, text: this.$t('common.reset', this.locale), selected: !value })
		},
		formatDate (value) {
			return value === '-' ? value : formatValue('Timestamp', value)
		},
		isNew (row) {
			return this.$refs.table && this.$refs.table.isNew(row)
		},
		id (row) {
			const { id, listName } = (row.row || row)
			return id + ',' + listName
		},
		isSelected (row) {
			return this.selected[this.id(row)]
		},
		select (row, value) {
			this.selected[this.id(row)] = value
			this.resetUpdatedKey()
		},
		setIds (pages, activePage) {
			if (pages[activePage]) {
				const ids = pages[activePage].map(r => r.row.key)
				if (!arrayEquals(ids, this.ids)) {
					this.ids = ids
				}
			}
		},
		selectAll (value) {
			if (value !== this.allSelected) {
				this.ids.forEach(id => {
					this.selected[id] = value
				})
			}
			this.resetUpdatedKey()
		},
		async removeSelected () {
			for (const e of Object.entries(this.selected)) {
				if (e[1]) {
					const [, id, listName] = /^([^,]+),(.+)$/.exec(e[0])
					await this.removeEntity(this.listsEntities.filter(e => e.id === id && e.listName === listName)[0])
				}
			}
		},
		async handleUpload () {
			await this.upload({
				definitionName: this.uploadDefinition,
				listName: this.uploadList,
				reason: this.uploadReason,
				files: this.uploadFiles
			})
		},
		resetUpdatedKey () {
			this.updatedKey = Math.floor(Math.random() * 100000000)
		},
		async add () {
			const list = await this.addList(this.newList)
			this.selectedList = list.name
			this.newList = null
		},
		async remove () {
			await this.removeList(this.selectedList)
			this.selectedList = null
		},
		changePage (page) {
			this.page = page
		},
		...mapActions('lists', ['fetchListsEntities', 'addList', 'removeList', 'addEntity', 'removeEntity', 'upload'])
	}
}
</script>

<style scoped>
.lists-table {
	margin-top: 2rem;
}
>>> input.form-control {
	margin-bottom: 0;
}
.zz {
	margin-top: -0.5rem;
}
</style>
